// src/oura/components/client/changeRequests.ts
//
// What the staff side needs to show everything the client started, on the
// Requests card and in its drawer sheets: the changes they asked for ("Ask
// for a change" on their Work page) and the calls they asked for. Who asked,
// the state words, the order, which ones are new, and a signal so the screen
// reads the list again straight after the drawer writes to it.
//
// What we asked the client is the other card (Questions to the client,
// asks.ts). Every state and date comes from the server's ChangeRequest and
// CallRequest. Nothing here decides one.

import { useEffect } from "react";
import { useUserProfileContext } from "@context/user_profile";
import {
    DEMO_STAFF,
    isClientReviewLive,
    malaysianMonthNow,
    type CallRequest,
    type ChangeRequest,
    type ChangeRequestError,
    type Notification,
} from "@service/aeo/clientReview";
import { REQUEST_STATUS, type StatusStyle } from "@oura/ui";
import { fromAgoText, requestStatusKey, smallChangeMonthText } from "@oura/docs";
import { dayDate } from "../../lib/oura";

/** The shared inbox a request lands in and every reply is sent from. */
export const OURA_INBOX = "oura@ourteam.ai";

/** The client's name as the request carries it. Never "You": staff always see who asked. */
export function requesterName(c: { requestedBy: string }): string {
    const name = c.requestedBy.trim();
    return name && name.toLowerCase() !== "you" ? name : "The client";
}

/**
 * The state tag: Needs us, Answered, not done, This month's small change or
 * Done. A small change filed in an earlier Malaysian calendar month and not
 * done yet names that month ("Small change for August 2026"), as the
 * merchant's "Your requests" does. `extra`: filed after the month already had
 * its small change (staff filed it anyway), "Extra small change".
 */
export function requestStatus(c: Pick<ChangeRequest, "status" | "smallChange">, month: string = malaysianMonthNow(), extra = false): StatusStyle {
    const key = requestStatusKey(c);
    const style = REQUEST_STATUS[key];
    if (key === "small_change" && c.smallChange) {
        const named = c.smallChange.month !== month ? smallChangeMonthText(c.smallChange.month) : null;
        if (extra) return { ...style, label: named ? `Extra small change for ${named}` : "Extra small change" };
        if (named) return { ...style, label: `Small change for ${named}` };
    }
    return style;
}

/**
 * A call request's state tag: Needs us while it is open, in the change
 * requests' own word and colour; then Scheduled, or Closed.
 */
export const CALL_STATUS: Record<CallRequest["status"], StatusStyle> = {
    open: REQUEST_STATUS.open,
    scheduled: { label: "Scheduled", tone: "green" },
    closed: { label: "Closed", tone: "gray" },
};

/** A state this build does not know reads as open: it is ours until someone says otherwise. */
export function callStatus(c: Pick<CallRequest, "status">): StatusStyle {
    return CALL_STATUS[c.status] ?? CALL_STATUS.open;
}

/** "Asked 3 hours ago", "Asked yesterday", "Asked Sun 27 Sep"; null for a missing date. */
export function askedText(at: string | null | undefined): string | null {
    const ago = fromAgoText(at);
    if (ago) return `Asked ${ago}`;
    const day = dayDate(at);
    return day ? `Asked ${day}` : null;
}

/** "Replied 2 hours ago", "Replied yesterday", "Replied Sat 26 Sep"; null for a missing date. */
export function repliedText(at: string | null | undefined): string | null {
    const ago = fromAgoText(at);
    if (ago) return `Replied ${ago}`;
    const day = dayDate(at);
    return day ? `Replied ${day}` : null;
}

/** "Angel replied from oura@ourteam.ai: Yes, from tomorrow." Null before a reply. */
export function replyLine(c: Pick<ChangeRequest, "reply">): string | null {
    if (!c.reply) return null;
    return `${c.reply.by || "We"} replied from ${OURA_INBOX}: ${c.reply.body}`;
}

/* ------------------------------------------------------------------------- *
 * The Requests card's groups
 * ------------------------------------------------------------------------- */

/**
 * Whether a change request waits on us: until it is Done. A reply ("Yes, by
 * Thursday") promises the change; it is still ours to make, so an answered
 * request keeps counting until someone marks it Done. The one rule the page
 * sentence, the Requests card and the Overview all count by.
 */
export function requestIsOurs(c: Pick<ChangeRequest, "status" | "smallChange">): boolean {
    return c.status !== "done";
}

/** One thing the client started: a change they asked for, or a call. */
export type ClientRequest =
    | { kind: "change"; id: string; change: ChangeRequest }
    | { kind: "call"; id: string; call: CallRequest };

export interface RequestGroups {
    /** Changes until they are Done (answered or not) and open calls: our move. */
    needsUs: ClientRequest[];
    /** Calls we scheduled: booked, and closed once held. */
    scheduled: ClientRequest[];
    /** Changes marked Done, and calls closed. */
    done: ClientRequest[];
}

const time = (iso: string | null | undefined) => (iso ? Date.parse(iso) || 0 : 0);
const asChange = (change: ChangeRequest): ClientRequest => ({ kind: "change", id: change.id, change });
const asCall = (call: CallRequest): ClientRequest => ({ kind: "call", id: call.id, call });
/** Newest first by the time that matters for the group. */
const newest = (at: (r: ClientRequest) => string | null | undefined) => (a: ClientRequest, b: ClientRequest) => time(at(b)) - time(at(a));

/** Everything the client started, split by who moves next, each part newest first. */
export function groupRequests(changes: readonly ChangeRequest[], calls: readonly CallRequest[]): RequestGroups {
    const calledOpen = calls.filter((c) => callStatus(c) === CALL_STATUS.open);
    return {
        // A request is ours from the moment it lands until it is Done. Ordered by its newest message (#4930).
        needsUs: [...changes.filter(requestIsOurs).map(asChange), ...calledOpen.map(asCall)]
            .sort(newest((r) => (r.kind === "change" ? r.change.lastAt ?? r.change.at : r.call.at))),
        scheduled: calls.filter((c) => c.status === "scheduled").map(asCall).sort(newest((r) => (r.kind === "call" ? r.call.at : null))),
        done: [
            ...changes.filter((c) => c.status === "done").map(asChange),
            ...calls.filter((c) => c.status === "closed").map(asCall),
        ].sort(newest((r) => (r.kind === "change" ? r.change.doneAt ?? r.change.at : r.call.at))),
    };
}

/* ------------------------------------------------------------------------- *
 * What the bell has not marked read: the "New" tags
 * ------------------------------------------------------------------------- */

export interface UnreadMarks {
    /** Questions to the client with an unread notice (a reply, an answer). */
    items: ReadonlySet<string>;
    /** Requests an unread notice links by id (`?request=`): "asked for a change" today. */
    changes: ReadonlySet<string>;
    /** The items unread "asked for a call" notices are about ("" for a call on no item). */
    callItems: ReadonlySet<string>;
}

export const NO_MARKS: UnreadMarks = { items: new Set(), changes: new Set(), callItems: new Set() };

/** A call notice names no call, only its item, so it is known by its words (UT-51 keeps them distinct). */
const CALL_NOTICE = /\basked for a call\b/i;

function requestIdIn(link: string | null | undefined): string | null {
    const q = link?.indexOf("?") ?? -1;
    return link && q >= 0 ? new URLSearchParams(link.slice(q + 1)).get("request") : null;
}

/**
 * Sorts the unread staff notices by what they are about. A call notice marks
 * its call, never the question it is on: the call has its own row now.
 */
export function unreadMarks(notices: readonly Notification[]): UnreadMarks {
    const items = new Set<string>();
    const changes = new Set<string>();
    const callItems = new Set<string>();
    for (const n of notices) {
        if (n.read) continue;
        const request = requestIdIn(n.link);
        if (request) changes.add(request);
        else if (CALL_NOTICE.test(n.title)) callItems.add(n.itemId ?? "");
        else if (n.itemId) items.add(n.itemId);
    }
    return { items, changes, callItems };
}

/** A request the bell still has unread. A call notice that links its call by id counts too. */
export function isFresh(r: ClientRequest, marks: UnreadMarks): boolean {
    return marks.changes.has(r.id) || (r.kind === "call" && marks.callItems.has(r.call.itemId ?? ""));
}

/* ------------------------------------------------------------------------- *
 * Replies and failures
 * ------------------------------------------------------------------------- */

/**
 * The name a staff reply is signed with, as the placeholder says it. The
 * sample store signs as the demo account manager; the server signs as the
 * person signed in.
 */
export function useStaffSignature(): string | null {
    const [, userInfo] = useUserProfileContext();
    if (!isClientReviewLive) return DEMO_STAFF.name;
    const name = [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" ").trim();
    return name || userInfo?.username || null;
}

/** One sentence for a request call that failed. The server's own codes get their own words. */
export function requestErrorText(err: unknown, fallback: string): string {
    const e = err as Partial<ChangeRequestError> | null;
    switch (e?.code) {
        case "reply_required":
            return "Write a reply first.";
        case "note_required":
            return "Say what you'd like changed.";
        case "note_too_long":
            return "Keep it to 2,000 characters or fewer.";
        case "file_not_yours":
            return "One of the files could not be attached. Remove it and add it again.";
        default:
            return e?.status === 404 ? "This request is no longer here." : fallback;
    }
}

/* ------------------------------------------------------------------------- *
 * "The list changed": the drawer writes, the screen reads again at once
 * instead of on its next poll.
 * ------------------------------------------------------------------------- */

const listeners = new Set<() => void>();

/** Tell every open requests list that a request was written. */
export function changeRequestsChanged(): void {
    Array.from(listeners).forEach((cb) => cb());
}

/** Call `cb` whenever a request is written from this tab. */
export function useOnChangeRequestsChanged(cb: () => void): void {
    useEffect(() => {
        listeners.add(cb);
        return () => {
            listeners.delete(cb);
        };
    }, [cb]);
}
